import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

const OVERVIEW_TTL_MS = 60_000;
const LIST_TTL_MS = 60_000;
const LIST_PAGE_LIMIT = 100;
const PAYMENTS_PAGE_LIMIT = 500;

/** @type {{ token: string|null, promise: Promise|null, result: any, at: number }} */
let overviewCache = { token: null, promise: null, result: null, at: 0 };
const listCache = new Map();

function extractRows(data, key) {
  const raw = data?.[key];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function runListDeduped(key, factory, { force = false, ttlMs = LIST_TTL_MS } = {}) {
  const hit = listCache.get(key);
  if (!force && hit?.result != null && Date.now() - hit.at < ttlMs) {
    return Promise.resolve(hit.result);
  }
  if (!force && hit?.promise) {
    return hit.promise;
  }

  const promise = Promise.resolve()
    .then(factory)
    .then((result) => {
      listCache.set(key, { at: Date.now(), result, promise: null });
      return result;
    })
    .catch((err) => {
      const current = listCache.get(key);
      if (current?.promise === promise) listCache.delete(key);
      throw err;
    });

  listCache.set(key, { at: hit?.at || 0, result: hit?.result ?? null, promise });
  return promise;
}

async function fetchAllPages(token, path, key, extra = {}) {
  const rows = [];
  let page = 1;
  let lastPage = 1;

  do {
    const params = new URLSearchParams({ page: String(page), limit: String(LIST_PAGE_LIMIT) });
    Object.entries(extra).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== "") params.set(name, String(value));
    });
    const json = await apiRequest(`${path}?${params}`, { method: "GET", token });
    const data = unwrapApiEnvelope(json);
    rows.push(...extractRows(data, key));
    lastPage = Number(data?.pagination?.last_page || 1);
    page += 1;
  } while (page <= lastPage && page <= 40);

  return rows;
}

function normalizePaymentType(value) {
  const key = String(value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (!key) return "—";
  if (
    key === "mtn" ||
    key === "mtnmomo" ||
    key === "telecel" ||
    key === "telecelcash" ||
    key === "vodafone" ||
    key === "airteltigo" ||
    key === "airtel" ||
    key === "at"
  ) {
    return "momo";
  }
  return value;
}

function namedPerson(...values) {
  const joined = [values[0]?.first, values[0]?.last].filter(Boolean).join(" ").trim();
  const candidates = joined ? [joined, ...values.slice(1)] : values;
  for (const value of candidates) {
    if (value && typeof value === "object") continue;
    const text = String(value ?? "").trim();
    if (text && text !== "—" && !/^customer$/i.test(text)) return text;
  }
  return "—";
}

export function normalizePayment(raw) {
  if (!raw) return null;
  const owed = Number(raw.outstanding ?? raw.outstanding_balance ?? raw.amount_owed ?? raw.amountOwed ?? 0);
  const paid = Number(raw.amount_paid ?? raw.amountPaid ?? raw.amount ?? 0);
  const customer = namedPerson(
    { first: raw.first_name, last: raw.last_name },
    raw.fullname,
    raw.customer_name,
    raw.customer
  );
  return {
    id: raw.reference_id || raw.payment_id || String(raw.id ?? ""),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    payment_id: raw.reference_id || raw.payment_id || null,
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    customer,
    caseId: raw.case_code || raw.case_id || raw.caseId || "—",
    amount: Number(raw.amount_paid ?? raw.amount ?? 0),
    amountOwed: owed,
    amountPaid: paid,
    outstandingAmount: Number(raw.outstanding ?? raw.outstandingAmount ?? Math.max(0, owed - paid)),
    paymentType: normalizePaymentType(raw.payment_method || raw.payment_type || raw.paymentType),
    method: raw.payment_method || raw.interaction_method || raw.method || "—",
    status: raw.status || "pending",
    reconciled: Boolean(raw.reconciled),
    comment: raw.comment || "",
    commentPresets: raw.comment_presets || raw.commentPresets || [],
    receivedAt: raw.updated_at || raw.created_at || raw.receivedAt,
    reference: raw.reference_id || raw.reference || "—",
    referenceId: raw.reference_id || raw.referenceId || null,
    msisdn: raw.msisdn || "",
    network: raw.network || "",
    providerStatus: raw.provider_status || raw.providerStatus || "",
    source: raw.source || "manual",
    description: raw.description || raw.comment || "",
  };
}

export function formatCallDuration(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" && /hour|minute/i.test(value)) return value;
  const total = Number(value);
  if (!Number.isFinite(total)) return String(value);
  const minutes = Math.max(0, Math.round(total));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (mins > 0 || hours === 0) parts.push(`${mins} ${mins === 1 ? "minute" : "minutes"}`);
  return parts.join(" ");
}

function presentValue(value) {
  if (value == null || value === "") return null;
  return String(value);
}

function dateOnly(value) {
  if (!value) return null;
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
}

const DISPLAY_DETAIL_KEYS = {
  phone_number: "Phone number",
  contact_result: "Contact result",
};

function parseOutcomePayload(value) {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string") return { label: "" };
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return { label: trimmed };
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : { label: trimmed };
  } catch {
    return { label: trimmed };
  }
}

export function normalizeCallLog(raw) {
  if (!raw) return null;
  const numericId = raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null;
  const summary = raw.summary || "";
  const outcomePayload = parseOutcomePayload(raw.outcome);
  const outcomeLabel = outcomePayload.label || (typeof raw.outcome === "string" && !String(raw.outcome).trim().startsWith("{") ? raw.outcome : "") || summary || "Logged";
  const followUpDate = dateOnly(raw.follow_up_date || raw.followUpDate || null);
  const followUpNotes = raw.follow_up_notes || raw.followUpNotes || "";
  const promised = Number(raw.promised_amount ?? raw.promisedAmount ?? 0);
  const dueDate = dateOnly(raw.promised_payment_date || raw.due_date || raw.dueDate || null);
  const phoneNumber = outcomePayload.phone_number || outcomePayload.whatsapp_number || null;
  const contactResult =
    outcomePayload.contact_result || raw.contact_results || raw.contact_result || null;
  const fields = [
    [DISPLAY_DETAIL_KEYS.phone_number, phoneNumber],
    [DISPLAY_DETAIL_KEYS.contact_result, contactResult],
  ]
    .map(([label, value]) => ({ label, value: presentValue(value) }))
    .filter((field) => field.value);

  return {
    id: numericId != null ? String(numericId) : String(raw.call_log_id ?? ""),
    numericId,
    agentId: raw.engagement_by != null ? String(raw.engagement_by) : "",
    customer: namedPerson(
      { first: raw.first_name, last: raw.last_name },
      raw.fullname,
      raw.customer_name,
      raw.customer
    ),
    caseId: raw.case_code || raw.case_id || raw.caseId || "—",
    agent: raw.agent || raw.engagement_by || "—",
    type: raw.call_type || raw.interaction_method || raw.type || "Call",
    phoneNumber: presentValue(phoneNumber),
    contactResult: presentValue(contactResult),
    outcome: outcomeLabel,
    loggedAt: raw.engagement_at || raw.logged_at || raw.created_at,
    promisedAmount: promised,
    dueDate,
    recordedBy: raw.recorded_by || raw.agent || "—",
    followUp: Boolean(followUpDate || followUpNotes),
    followUpDate,
    followUpNotes,
    summary,
    outcomeDetails: {
      summary,
      fields,
      followUpDate,
      followUpNotes,
    },
  };
}

export async function fetchOpsDashboardOverview(token) {
  if (USE_MOCK_AUTH || !token) return null;

  const now = Date.now();
  if (overviewCache.token === token && overviewCache.result && now - overviewCache.at < OVERVIEW_TTL_MS) {
    return overviewCache.result;
  }
  if (overviewCache.token === token && overviewCache.promise) {
    return overviewCache.promise;
  }

  const promise = (async () => {
    const json = await apiRequest(OPS_AUTH.dashboardOverview, { method: "GET", token });
    const data = unwrapApiEnvelope(json);
    overviewCache = { token, promise: null, result: data, at: Date.now() };
    return data;
  })().catch((err) => {
    if (overviewCache.promise === promise) {
      overviewCache = { token: null, promise: null, result: null, at: 0 };
    }
    throw err;
  });

  overviewCache = { token, promise, result: null, at: 0 };
  return promise;
}

export function clearOpsDashboardOverviewCache() {
  overviewCache = { token: null, promise: null, result: null, at: 0 };
}

export async function fetchOpsFollowUps(token) {
  if (USE_MOCK_AUTH || !token) return { pending: 0, upcoming: 0, total: 0, tasks: [] };
  const json = await apiRequest(OPS_AUTH.followUps, { method: "GET", token });
  const data = unwrapApiEnvelope(json) || {};
  return {
    pending: Number(data.pending || 0),
    upcoming: Number(data.upcoming || 0),
    total: Number(data.total || 0),
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    team_follow_ups: data.team_follow_ups === true,
  };
}

export async function fetchOpsDashboardReport(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runListDeduped(
    `ops-dashboard-report:${token}`,
    async () => {
      const json = await apiRequest(OPS_AUTH.dashboardReport, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchOpsPayments(token, extra = {}, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return [];
  return runListDeduped(
    `ops-payments:${token}:${JSON.stringify(extra)}`,
    async () => {
      const rows = await fetchAllPages(token, OPS_AUTH.payments, "payments", {
        ...extra,
        limit: PAYMENTS_PAGE_LIMIT,
      });
      return rows.map(normalizePayment).filter(Boolean);
    },
    { force }
  );
}

export async function fetchOpsPaymentsSummary(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runListDeduped(
    `ops-payments-summary:${token}`,
    async () => {
      const json = await apiRequest(OPS_AUTH.paymentsSummary, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchOpsCallLogs(token, extra = {}, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return [];
  return runListDeduped(
    `ops-call-logs:${token}:${JSON.stringify(extra)}`,
    async () => {
      const rows = await fetchAllPages(token, OPS_AUTH.callLogs, "call_logs", extra);
      return rows.map(normalizeCallLog).filter(Boolean);
    },
    { force }
  );
}

export async function fetchOpsCallLogsSummary(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runListDeduped(
    `ops-call-logs-summary:${token}`,
    async () => {
      const json = await apiRequest(OPS_AUTH.callLogsSummary, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}
